(() => {
  if (customElements.get('zj-newsletter-popup')) return;

  class ZJNewsletterPopup extends HTMLElement {
    static active = null;
    static dismissed = false;
    static pending = false;
    static shown = false;

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.listeners = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-zj-popup-config]').textContent);
      this.dialog = this.querySelector('dialog');
      this.launcher = this.querySelector('.zj-newsletter-popup__launcher');
      this.form = this.querySelector('form');
      this.email = this.form.elements.email;
      this.birthday = this.form.elements.Birthday;
      this.submit = this.form.querySelector('[type="submit"]');
      this.status = this.querySelector('[role="status"]');
      this.preview = new URLSearchParams(location.search).get('zj_popup_preview') === '1';
      this.pending = false;
      this.succeeded = false;
      const options = { signal: this.listeners.signal };
      this.launcher.addEventListener('click', () => this.open({ manual: true }), options);
      window.addEventListener('storage', event => {
        if (event.key === 'zj-newsletter-popup-suppression' || event.key === null) this.updateLauncher();
      }, options);
      document.addEventListener('visibilitychange', () => this.updateLauncher(), options);
      window.addEventListener('pagehide', () => {
        clearTimeout(this.timer);
        this.dialog.close();
      }, options);
      this.form.addEventListener('submit', event => this.subscribe(event), options);
      this.querySelector('.zj-newsletter-popup__close').addEventListener('click', () => this.dismiss(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.dismiss(); }, options);
      this.dialog.addEventListener('close', () => this.release(), options);
      this.dialog.addEventListener('keydown', event => this.trapFocus(event), options);
      this.form.addEventListener('input', event => {
        event.target.removeAttribute('aria-invalid');
        if (!this.pending) this.status.textContent = '';
      }, options);
      this.closest('.shopify-section')?.addEventListener('shopify:section:select', () => {
        if (this.preview) this.open();
      }, options);
      if (!this.preview && !this.config.enabled) return;
      this.configureAppearance();
      this.updateLauncher();
      if (!this.preview && this.autoSuppressed()) return;
      this.timer = setTimeout(() => this.open(), this.preview ? 0 : Number(this.config.display_delay) * 1000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.listeners?.abort();
      this.dialog?.close();
      this.release();
      if (this.appearance) document.adoptedStyleSheets = document.adoptedStyleSheets.filter(sheet => sheet !== this.appearance);
      this.initialized = false;
    }

    configureAppearance() {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync('.zj-newsletter-popup {}');
      const style = sheet.cssRules[0].style;
      style.setProperty('--zj-popup-width', `${Math.min(1300, Math.max(900, Number(this.config.desktop_width) || 1152))}px`);
      style.setProperty('--zj-popup-spacing', `${Math.min(32, Math.max(12, Number(this.config.responsive_spacing) || 20))}px`);
      for (const [setting, variable] of Object.entries({ background: 'background', text_color: 'text', border_color: 'border', button_background: 'button-background', button_color: 'button-text' })) {
        if (CSS.supports('color', this.config[setting])) style.setProperty(`--zj-popup-${variable}`, this.config[setting]);
      }
      this.appearance = sheet;
      document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
    }

    readStorage(key, session = false) {
      try {
        const value = (session ? sessionStorage : localStorage).getItem(key);
        if (value) return value;
      } catch {}
      try {
        const cookie = document.cookie.split('; ').find(value => value.startsWith(`${key}=`));
        return cookie ? decodeURIComponent(cookie.slice(key.length + 1)) : null;
      } catch { return null; }
    }

    writeStorage(key, value, days) {
      try {
        (days ? localStorage : sessionStorage).setItem(key, value);
      } catch {}
      try {
        document.cookie = `${key}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${days ? `; Max-Age=${days * 86400}` : ''}${location.protocol === 'https:' ? '; Secure' : ''}`;
      } catch {}
    }

    suppressionData() {
      try {
        return JSON.parse(this.readStorage('zj-newsletter-popup-suppression') || '{}') || {};
      } catch { return {}; }
    }

    suppressed() {
      const data = this.suppressionData();
      return Math.max(Number(data.closed) || 0, Number(data.success) || 0) > Date.now();
    }

    autoSuppressed() {
      return ZJNewsletterPopup.dismissed || ZJNewsletterPopup.shown || this.suppressed() || this.readStorage('zj-newsletter-popup-session', true) === '1';
    }

    suppress(kind) {
      if (this.preview) return;
      const days = kind === 'success' ? 90 : 7;
      const data = this.suppressionData();
      data[kind] = Math.max(Number(data[kind]) || 0, Date.now() + days * 86400000);
      this.writeStorage('zj-newsletter-popup-suppression', JSON.stringify(data), 90);
    }

    updateLauncher() {
      this.launcher.hidden = (!this.preview && !this.config.enabled) || this.dialog.open;
    }

    open({ manual = false } = {}) {
      if (!this.isConnected || this.dialog.open || ZJNewsletterPopup.active || (!this.preview && (!this.config.enabled || (!manual && this.autoSuppressed())))) return;
      clearTimeout(this.timer);
      this.previousFocus = document.activeElement;
      ZJNewsletterPopup.active = this;
      this.ownsScrollLock = !document.documentElement.classList.contains('zj-newsletter-popup-scroll-lock');
      document.documentElement.classList.add('zj-newsletter-popup-scroll-lock');
      this.dialog.showModal();
      this.manualOpen = manual;
      if (!this.preview) {
        ZJNewsletterPopup.shown = true;
        this.writeStorage('zj-newsletter-popup-session', '1');
      }
      this.updateLauncher();
      this.email.focus({ preventScroll: true });
    }

    dismiss() {
      if (!this.preview) ZJNewsletterPopup.dismissed = true;
      if (!this.succeeded && (!this.manualOpen || !this.suppressed())) this.suppress('closed');
      this.dialog.close();
    }

    release() {
      if (ZJNewsletterPopup.active !== this) return;
      ZJNewsletterPopup.active = null;
      if (this.ownsScrollLock) document.documentElement.classList.remove('zj-newsletter-popup-scroll-lock');
      this.updateLauncher();
      if (this.previousFocus?.isConnected && this.previousFocus !== document.body) this.previousFocus.focus({ preventScroll: true });
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const controls = [...this.dialog.querySelectorAll('input,button,[tabindex="0"]')].filter(element => !element.disabled && !element.closest('[inert]') && element.getClientRects().length);
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !controls.includes(document.activeElement))) {
        event.preventDefault(); first?.focus();
      }
    }

    invalid(field, message) {
      field.setAttribute('aria-invalid', 'true');
      this.status.textContent = message;
      field.focus({ preventScroll: true });
    }

    validBirthday(value) {
      if (!/^\d{2}\/\d{2}$/.test(value)) return false;
      const [month, day] = value.split('/').map(Number);
      return month >= 1 && month <= 12 && day >= 1 && day <= [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
    }

    payload(email, birthday) {
      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Zodiac Jules custom newsletter popup',
            profile: {
              data: {
                type: 'profile',
                attributes: { email, properties: { Birthday: birthday }, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: 'YkYp7q' } } }
        }
      };
    }

    async subscribe(event) {
      event.preventDefault();
      if (this.pending || this.succeeded) return;
      if (ZJNewsletterPopup.pending) { this.status.textContent = this.config.loading_message; return; }
      const email = this.email.value.trim();
      const birthday = this.birthday.value;
      this.email.value = email;
      this.email.removeAttribute('aria-invalid');
      this.birthday.removeAttribute('aria-invalid');
      if (!email || !this.email.validity.valid || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return this.invalid(this.email, this.config.invalid_email);
      if (!this.validBirthday(birthday)) return this.invalid(this.birthday, this.config.invalid_birthday);
      if (this.preview) { this.status.textContent = this.config.preview_message; return; }
      this.pending = true;
      ZJNewsletterPopup.pending = true;
      this.submit.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = this.config.loading_message;
      const timeout = new AbortController();
      const timer = setTimeout(() => timeout.abort(), 20000);
      try {
        const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=VLgGk3', {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify(this.payload(email, birthday)),
          credentials: 'omit',
          signal: timeout.signal
        });
        if (response.status === 202) {
          this.succeeded = true;
          ZJNewsletterPopup.dismissed = true;
          this.suppress('success', this.config.success_days);
          const offer = this.querySelector('[data-zj-popup-offer]');
          offer.inert = true;
          offer.setAttribute('aria-hidden', 'true');
          this.dataset.success = 'true';
          const success = this.querySelector('[data-zj-popup-success]');
          success.hidden = false;
          this.dialog.setAttribute('aria-labelledby', 'zj-popup-success-title');
          success.querySelector('h2').id = 'zj-popup-success-title';
          if (this.isConnected && this.dialog.open) success.focus({ preventScroll: true });
        } else if (response.status === 429) {
          this.status.textContent = this.config.rate_limit_error;
        } else {
          const body = await response.json().catch(() => null);
          const pointers = body?.errors?.map(error => error.source?.pointer || '') || [];
          if (pointers.some(pointer => pointer.endsWith('/email'))) this.invalid(this.email, this.config.invalid_email);
          else if (pointers.some(pointer => pointer.endsWith('/Birthday'))) this.invalid(this.birthday, this.config.invalid_birthday);
          else this.status.textContent = this.config.general_error;
        }
      } catch { this.status.textContent = this.config.general_error; }
      finally {
        clearTimeout(timer);
        this.pending = false;
        ZJNewsletterPopup.pending = false;
        this.submit.disabled = this.succeeded;
        this.form.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('zj-newsletter-popup', ZJNewsletterPopup);
})();
